import { useState } from 'react'
import { mdiAlertOutline, mdiCheck, mdiContentCopy } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'

/**
 * SecretReveal shows a secret the server answers once (a webhook signing
 * secret): monospace, selectable, with a copy button and the line saying it
 * will not be shown again. The caller drops the value from its state when the
 * reveal closes, so a reload never brings it back (patterns §54).
 */
export function SecretReveal({ secret, onceLabel, copyLabel, copiedLabel }: {
    secret: string
    onceLabel: string
    copyLabel: string
    copiedLabel: string
}) {
    const [copied, setCopied] = useState(false)
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(secret)
            setCopied(true)
        } catch {
            setCopied(false)
        }
    }
    return (
        <div className="space-y-2" data-testid="secret-reveal">
            <div className="flex items-center gap-2 rounded-md border border-warning/25 bg-warning/10 p-2">
                <code className="flex-1 break-all font-mono text-sm select-all" data-testid="webhook-secret-reveal">{secret}</code>
                <Button type="button" variant="outline" size="sm" onClick={copy} aria-label={copyLabel} title={copyLabel} data-testid="webhook-secret-copy-button">
                    <AppIcon path={copied ? mdiCheck : mdiContentCopy} size="sm" className="md:mr-1" />
                    <span className="hidden md:inline">{copied ? copiedLabel : copyLabel}</span>
                </Button>
            </div>
            <p className="flex items-center gap-1 text-sm text-warning" data-testid="secret-reveal-once">
                <AppIcon path={mdiAlertOutline} size="sm" />
                {onceLabel}
            </p>
        </div>
    )
}
